import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../designSystem/buttons/PrimaryButton";

const ContactUsComponent = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    number: "",
    email: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = ({ target }) => {
    const { name, value, type, checked } = target;

    setForm((previousForm) => ({
      ...previousForm,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((previousErrors) => ({ ...previousErrors, [name]: "" }));
    setSubmitStatus("");
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!form.number.trim()) nextErrors.number = "Number is required";
    if (!form.email.trim()) nextErrors.email = "Email is required";
    if (!form.message.trim()) nextErrors.message = "Message is required";
    if (!form.consent) {
      nextErrors.consent = "Please confirm you have read the privacy policy";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/send-adviser-contact-confirmation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name.trim(),
            number: form.number.trim(),
            email: form.email.trim(),
            message: form.message.trim(),
            to: (process.env.REACT_APP_ADVISER_EMAIL || "").trim(),
          }),
        },
      );

      if (!response.ok) throw new Error("Contact form request failed");

      setForm({
        name: "",
        number: "",
        email: "",
        message: "",
        consent: false,
      });
      setErrors({});
      setSubmitStatus("Thanks, your message has been sent successfully.");
    } catch (error) {
      setSubmitStatus(
        "Sorry, your message could not be sent. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact-us"
      className="background-container bg-white contact-section"
    >
      <div className="content-container column-center row grid2 gap-xxxxl">
        {/* <div className="column gap-s">
        <div className="content-semibold primary uppercase align-left">
          contact
        </div>
        </div> */}{" "}
        <div className="contact-details-column" aria-label="Contact details">
          <div className="contact-location-copy align-left">
            <h2 className="h4 primary">Contact Details</h2>
            <div className="contact-details">
              <div className="contact-detail">
                <span className="body2-semibold text-primary-1 uppercase">
                  Email
                </span>
                <a
                  className="body1 text-gray-1 contact-link"
                  href="mailto:enquiries@tagmortgages.co.uk"
                >
                  enquiries@tagmortgages.co.uk
                </a>
              </div>
              <div className="contact-detail">
                <span className="body2-semibold text-primary-1 uppercase">
                  Telephone
                </span>
                <a
                  className="body1 text-gray-1 contact-link"
                  href="tel:+441789777969"
                >
                  01789 777969
                </a>
              </div>
              <div className="contact-detail">
                <span className="body2-semibold text-primary-1 uppercase">
                  Address
                </span>
                <a
                  className="body1 text-gray-1 contact-link"
                  href="https://www.google.com/maps/search/?api=1&query=7%20Arrow%20Court%2C%20Alcester%2C%20B49%206PU"
                  target="_blank"
                  rel="noreferrer"
                >
                  TAG Mortgages, 7 Arrow Court, Alcester, B49 6PU
                </a>
              </div>
            </div>
          </div>
          <iframe
            className="contact-map"
            title="Map showing TAG Mortgages, 7 Arrow Court, Alcester, B49 6PU"
            src="https://www.google.com/maps?q=7%20Arrow%20Court%2C%20Alcester%2C%20B49%206PU&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        <div
          className="column gap-l contact-form-column"
          aria-label="Contact form"
        >
          <h2 className="h4 primary">Get in Touch</h2>
          <form className="column gap-m" onSubmit={handleSubmit} noValidate>
            <div className="column gap-s">
              <label
                className="body2-semibold text-primary-1"
                htmlFor="contact-name"
              >
                Name*
              </label>
              <input
                id="contact-name"
                className="width-100"
                name="name"
                value={form.name}
                onChange={handleChange}
                autoComplete="name"
              />
              {errors.name && (
                <span className="body2 text-error">{errors.name}</span>
              )}
            </div>{" "}
            <div className="column gap-s">
              <label
                className="body2-semibold text-primary-1"
                htmlFor="contact-email"
              >
                Email*
              </label>
              <input
                id="contact-email"
                className="width-100"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
              {errors.email && (
                <span className="body2 text-error">{errors.email}</span>
              )}
            </div>
            <div className="column gap-s">
              <label
                className="body2-semibold text-primary-1"
                htmlFor="contact-number"
              >
                Number*
              </label>
              <input
                id="contact-number"
                className="width-100"
                name="number"
                type="tel"
                value={form.number}
                onChange={handleChange}
                autoComplete="tel"
              />
              {errors.number && (
                <span className="body2 text-error">{errors.number}</span>
              )}
            </div>
            <div className="column gap-s">
              <label
                className="body2-semibold text-primary-1"
                htmlFor="contact-message"
              >
                Message*
              </label>
              <textarea
                id="contact-message"
                className="width-100"
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={8}
              />
              {errors.message && (
                <span className="body2 text-error">{errors.message}</span>
              )}
            </div>
            <div className="row-flex gap-s width-100">
              <input
                id="contact-consent"
                name="consent"
                type="checkbox"
                checked={form.consent}
                onChange={handleChange}
              />
              <label className="body2 text-primary-1" htmlFor="contact-consent">
                *I confirm I have read the{" "}
                <span
                  type="button"
                  onClick={() => navigate("/privacy-policy")}
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Privacy Policy
                </span>
                .
              </label>
            </div>
            {errors.consent && (
              <span className="body2 text-error">{errors.consent}</span>
            )}
            <PrimaryButton
              type="submit"
              text={isSubmitting ? "SENDING..." : "SUBMIT"}
              disabled={isSubmitting}
              onClick={handleSubmit}
            />
            {submitStatus && (
              <div
                className={`body2 ${
                  submitStatus.startsWith("Thanks")
                    ? "text-success"
                    : "text-error"
                }`}
                role="status"
              >
                {submitStatus}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactUsComponent;
